/**
 * Submissions API — atomic save-or-submit and status transitions.
 */
import { supabase } from '@/lib/supabase/client'
import {
  ConflictError,
  FormLockedError,
  RecordLockedError,
} from './errors'
import type { SubmitFormArgs, SubmitFormResult } from './types'

/**
 * Atomic save-or-submit. Replaces the prior pattern of (UPDATE submissions)
 * followed by a separate (UPDATE records SET status='in_progress') — both
 * now happen inside one Postgres transaction in the `submit_form` RPC.
 */
export async function submitForm(args: SubmitFormArgs): Promise<SubmitFormResult> {
  const { data, error } = await supabase.rpc('submit_form', {
    p_submission_id:      args.submissionId,
    p_record_id:          args.recordId,
    p_form_id:            args.formId,
    p_form_version_id:    args.formVersionId,
    p_instance_index:     args.instanceIndex,
    p_expected_version:   args.expectedVersion,
    p_user_id:            args.userId,
    p_project_id:         args.projectId,
    p_field_values:       args.fieldValues,
    p_soft_warning_flags: args.softWarningFlags,
    p_submit:             args.submit,
  })
  if (error) throw error

  const result = data as {
    success?: boolean
    error?: string
    submission_id?: string
    new_version?: number
    instance_index?: number
    record_status_changed?: boolean
    record_reopened?: boolean
    current_version?: number
    current_status?: string
  }

  if (result?.error === 'conflict') {
    throw new ConflictError(result.current_version ?? 0, result.current_status ?? 'unknown')
  }
  if (result?.error === 'record_locked') {
    throw new RecordLockedError(result.current_status ?? 'unknown')
  }
  if (result?.error === 'form_locked') {
    throw new FormLockedError()
  }
  if (result?.error === 'forbidden') {
    throw new Error('دسترسی غیرمجاز')
  }
  if (
    result?.error === 'submission_not_found' ||
    result?.error === 'record_not_found' ||
    result?.error === 'form_not_found'
  ) {
    throw new Error('سابقه یا فرم یافت نشد')
  }
  // A single-instance form whose one slot already exists, reached without its
  // submission id. Not a race — the record lock rules that out — so the fix is
  // to reload and edit the existing submission rather than retry.
  if (result?.error === 'instance_exists') {
    throw new Error('این فرم قبلاً برای این سابقه ثبت شده است؛ صفحه را تازه کنید.')
  }
  if (result?.error) {
    throw new Error(result.error)
  }

  return {
    submissionId: result.submission_id!,
    newVersion: result.new_version!,
    // The server assigns this under the record lock; the value we sent was a
    // guess and may have lost a race.
    instanceIndex: result.instance_index!,
    recordStatusChanged: !!result.record_status_changed,
    recordReopened: !!result.record_reopened,
  }
}

/**
 * Soft-delete a draft submission. RPC enforces:
 *   - submission must exist and have status='draft'
 *   - caller must be data_entry / admin / super
 *   - record must not be in submitted_for_review / under_review / approved
 *
 * Reuses the existing is_deleted/deleted_at/deleted_by columns so the row
 * stays in the audit trail.
 */
export async function discardDraft(submissionId: string, userId: string): Promise<void> {
  const { data, error } = await supabase.rpc('discard_draft', {
    p_submission_id: submissionId,
    p_user_id:       userId,
  })
  if (error) throw error
  const result = data as {
    success?: boolean
    error?: string
    current_status?: string
  }
  if (result?.error === 'submission_not_found') throw new Error('پیش‌نویس یافت نشد')
  if (result?.error === 'not_a_draft') throw new Error('فقط پیش‌نویس قابل حذف است')
  if (result?.error === 'forbidden') throw new Error('دسترسی غیرمجاز')
  if (result?.error === 'record_locked') {
    throw new RecordLockedError(result.current_status ?? 'unknown')
  }
  if (result?.error) throw new Error(result.error)
}

/** Called when an approver opens a record — submitted_for_review → under_review */
export async function transitionToUnderReview(
  recordId: string,
  projectId: string,
  userId: string,
  _userRole: string,
) {
  const { data, error } = await supabase.rpc('set_record_under_review', {
    p_record_id: recordId,
    p_user_id: userId,
    p_project_id: projectId,
  })
  if (error) throw error
  const result = data as { changed?: boolean; error?: string }
  if (result?.error === 'forbidden') throw new Error('forbidden')
}
